import { mdiClose, mdiTrashCanOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { Box, Button, Checkbox, Dialog, FormControlLabel, Grid, IconButton, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSnackbar } from "../../../components/context/SnackbarContext";
import useAxios from "../../../hooks/useAxios";
import { useLoader } from "../../../components/context/LoaderContext";
import { FieldArray, Formik } from "formik";
import * as yup from "yup";

function CreateNewCustomFieldDialog({ onClose, open, handleReloadFunc }) {
    const axiosInstance = useAxios();
    const { showSnackbar } = useSnackbar();
    const { showLoader, hideLoader } = useLoader();
    //const [jsonExampleData, setJsonExampleData] = useState([{ "label": "Online", "value": "online" }, { "label": "Offline", "value": "offline" }]);
    const [customFields, setCustomFields] = useState([]);

    useEffect(() => {
        async function fetchData() {
            getData();
        }
        fetchData();
    }, []);


    const getData = async () => {
        showLoader();
        await axiosInstance.get('api/custom-field/create').then((response) => {
            if (response.status === 200) {
                setCustomFields(response?.data?.data?.records || []);
                hideLoader();
            }
        })
            .then((json) => { })
            .catch((error) => {
                if (error.response) {
                    hideLoader();
                    if (error.response.status === 400) {
                        showSnackbar(error.response.data.message, "warning");
                    }
                }
            });
    }

    const buildInitialValues = () => {
        const initial = {
            model: '',
            field_name: '',
            placeholder_text: '',
            json_data_value: '',
            field_size: '',
            label: '',
            display_type: '',
            display_at: 'all',
            display_field_type: 'both',
            options: [{ label: '', value: '' }], // initial empty option
            is_multiple: 'no',
            is_required: 'no',
            collect_data_on_add: 'yes',
            max_input: '',
            min_input: '',
        };
        return initial;
    };

    const buildValidationSchema = () => {
        const schema = {
            model: yup
                .string()
                .required('Select the model this field belongs to'),

            field_name: yup
                .string()
                .required('Field Name is required')
                .min(2, "The field name must be at least 2 characters.")
                .max(50, "The field name must not be greater than 50 characters.")
                .matches(
                    /^[a-zA-Z_ ]+$/,
                    'Only letters, spaces, and underscores allowed'
                ),

            label: yup
                .string()
                .required('Field Label is required')
                .min(2, "The lable must be at least 2 characters.")
                .max(50, "The lable must not be greater than 50 characters."),

            display_type: yup
                .string()
                .required('Select a field type'),

            placeholder_text: yup
                .string()
                .nullable()
                .min(2, "The place holder must be at least 2 characters.")
                .max(50, "The place holder must not be greater than 50 characters."),

            options: yup
                .array()
                .of(
                    yup.object().shape({
                        label: yup.string().required('Label is required'),
                        value: yup.string().required('Value is required'),
                    })
                )
                .nullable()
                .when('display_type', {
                    is: (val) => ['checkbox', 'select', 'multi_select', 'radio'].includes(val),
                    then: (schema) => schema.min(1, 'At least one option is required'),
                    otherwise: () => yup.array().nullable().notRequired(),
                }),

            json_data_value: yup
                .string()
                .nullable()
                .test('is-valid-json', 'Invalid JSON format', function (value) {
                    const { display_type } = this.parent;
                    if (!['checkbox', 'select', 'multi_select', 'radio'].includes(display_type)) {
                        return true; // skip JSON validation
                    }
                    if (!value) return true;
                    try {
                        JSON.parse(value);
                        return true;
                    } catch (err) {
                        return false;
                    }
                }),


            field_size: yup
                .number()
                .typeError('Field size must be a number')
                .max(255, 'Field size cannot be more than 255')
                .nullable(),

            max_input: yup
                .number()
                .typeError('Max input must be a number')
                .max(255, 'Max input cannot be more than 255')
                .nullable(),

            min_input: yup
                .number()
                .typeError('Min input must be a number')
                .nullable(),

            is_required: yup
                .string()
                .oneOf(['yes', 'no'], 'Invalid selection')
                .required(),

            is_multiple: yup
                .string()
                .oneOf(['yes', 'no'], 'Invalid selection')
                .required(),

            collect_data_on_add: yup
                .string()
                .oneOf(['yes', 'no'], 'Invalid selection')
                .required(),


        };
        return yup.object().shape(schema);
    };


    const handleSubmit = async (values, onSubmitProps) => {
        const formData = new FormData();

        // Transform the options into JSON string for backend
        const payload = {
            ...values,
            json_data_value: JSON.stringify(values.options || []),
        };

        // Optional: remove options from payload before appending
        delete payload.options;

        // Append all fields to formData
        Object.entries(payload).forEach(([key, value]) => {
            formData.append(key, value);
        });

        try {
            showLoader();
            const response = await axiosInstance.post('/api/custom-field/store', formData);
            if (response.status === 200) {
                hideLoader()
                showSnackbar(response.data.message, 'success');
                onClose();
                handleReloadFunc();
            }
        } catch (error) {
            hideLoader();
            if (error.response) {
                if (error.response.status === 422 || error.response.data.validation_errors) {
                    const errors = error.response.data.validation_errors;
                    Object.keys(errors).forEach(key => {
                        onSubmitProps.setFieldError(key, errors[key]);
                    });
                } else {
                    showSnackbar(error.response.data.message, 'error');
                }
            } else {

            }
        } finally {
            hideLoader();
        }
    }

    const formatFieldType = (type) => {
        return type
            .replace(/_/g, ' ')           // Replace underscores with spaces
            .replace(/[^a-zA-Z0-9 ]/g, '') // Remove any other special characters
            .replace(/\b\w/g, (c) => c.toUpperCase()); // Capitalize first letter of each word
    };

    const DISPLAY_TYPES_WITH_OPTIONS = ['select', 'multi_select', 'radio', 'checkbox'];
    const DISPLAY_MIN_MAX_LENGTH_OPTIONS = ['text', 'number', 'email'];
    const DISPLAY_ALLOW_MULTIPLE_OPTIONS = ['select', 'file'];
    const DISPLAY_HIDE_OPTIONS = ['textarea', 'checkbox', 'radio', 'date', 'time', 'datetime', 'file'];
    const DISPLAY_HIDE_MODEL_OPTIONS = ['quote_item'];


    return (
        <>
            <Dialog onClose={onClose} open={open} maxWidth={'md'}>
                <div className="dialog-title">
                    <h2>Create Custom Field</h2>
                    <Tooltip title="Close">
                        <button className="icon-button dialog-close" onClick={onClose}>
                            <Icon path={mdiClose} color={'currentColor'} size={1} />
                        </button>
                    </Tooltip>
                </div>
                <Formik
                    initialValues={buildInitialValues()}
                    validationSchema={buildValidationSchema()}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {props => {
                        const {
                            touched,
                            errors,
                            values,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            setFieldValue
                        } = props;

                        return (
                            <form onSubmit={handleSubmit}>
                                <TextField
                                    select
                                    label="Field Belongs To"
                                    value={values.model}
                                    name="model"
                                    variant="filled"
                                    //onChange={handleChange}
                                    onChange={(e) => {
                                        const selectedModel = e.target.value;

                                        setFieldValue('model', selectedModel); // Update model

                                        // Check if current display_type is invalid for this model
                                        if (
                                            DISPLAY_HIDE_MODEL_OPTIONS.includes(selectedModel) &&
                                            DISPLAY_HIDE_OPTIONS.includes(values.display_type)
                                        ) {
                                            // Reset display_type to empty if it's invalid for this model
                                            setFieldValue('display_type', '');
                                        }
                                    }}
                                    onBlur={handleBlur}
                                    error={errors.model && touched.model}
                                    helperText={(errors.model && touched.model) && errors.model}
                                >
                                    {
                                        customFields?.modelList && customFields?.modelList?.length > 0 ?
                                            customFields?.modelList.map((model, index) => (
                                                <MenuItem key={index} value={model}>
                                                    {formatFieldType(model)}
                                                </MenuItem>
                                            )) :
                                            <MenuItem value={-1}></MenuItem>
                                    }
                                </TextField>

                                <TextField
                                    select
                                    label="Field Type"
                                    value={values.display_type}
                                    name="display_type"
                                    variant="filled"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.display_type && touched.display_type}
                                    helperText={(errors.display_type && touched.display_type) && errors.display_type}
                                >
                                    {
                                        customFields?.fieldTypes && customFields.fieldTypes.length > 0 ? (
                                            customFields.fieldTypes.map((type, index) => {
                                                const isDisabled =
                                                    DISPLAY_HIDE_MODEL_OPTIONS.includes(values.model) &&
                                                    DISPLAY_HIDE_OPTIONS.includes(type);
                                                return (
                                                    <MenuItem key={index} value={type} disabled={isDisabled}>
                                                        {formatFieldType(type)}
                                                    </MenuItem>
                                                );
                                            })
                                        ) : (
                                            <MenuItem value={-1}>
                                                No options available
                                            </MenuItem>
                                        )
                                    }
                                </TextField>

                                <TextField
                                    label="Field Name"
                                    variant="filled"
                                    name="field_name"
                                    value={values?.field_name || ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    // error={errors.field_name && touched.field_name}
                                    // helperText={(errors.field_name && touched.field_name) && errors.field_name}
                                    error={Boolean(errors.field_name && touched.field_name)}
                                    helperText={touched.field_name && errors.field_name}
                                />

                                <TextField
                                    label="Field Label"
                                    variant="filled"
                                    name="label"
                                    value={values?.label || ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.label && touched.label}
                                    helperText={(errors.label && touched.label) && errors.label}
                                />

                                <TextField
                                    label="Placeholder Text"
                                    variant="filled"
                                    name="placeholder_text"
                                    value={values?.placeholder_text || ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.placeholder_text && touched.placeholder_text}
                                    helperText={(errors.placeholder_text && touched.placeholder_text) && errors.placeholder_text}
                                />


                                {/* <TextField
                                    label="Json Data Value"
                                    variant="filled"
                                    multiline
                                    rows={3}
                                    name="json_data_value"
                                    value={values?.json_data_value || ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.json_data_value && touched.json_data_value}
                                    helperText={(errors.json_data_value && touched.json_data_value) && errors.json_data_value}
                                /> */}
                                {DISPLAY_TYPES_WITH_OPTIONS.includes(values.display_type) && (
                                    <FieldArray name="options">
                                        {({ push, remove }) => (
                                            <Box sx={{ mb: 3 }}>
                                                <h3>Options (Label & Value)</h3>
                                                {values.options && values.options.length > 0 ? (
                                                    values.options.map((option, index) => (
                                                        <Box
                                                            key={index}
                                                            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                                                        >
                                                            <TextField
                                                                name={`options[${index}].label`}
                                                                label="Label"
                                                                variant="filled"
                                                                value={option.label}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                error={
                                                                    touched.options &&
                                                                    touched.options[index] &&
                                                                    Boolean(errors.options?.[index]?.label)
                                                                }
                                                                helperText={
                                                                    touched.options &&
                                                                    touched.options[index] &&
                                                                    errors.options?.[index]?.label
                                                                }
                                                            />

                                                            <TextField
                                                                name={`options[${index}].value`}
                                                                label="Value"
                                                                variant="filled"
                                                                value={option.value}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                error={
                                                                    touched.options &&
                                                                    touched.options[index] &&
                                                                    Boolean(errors.options?.[index]?.value)
                                                                }
                                                                helperText={
                                                                    touched.options &&
                                                                    touched.options[index] &&
                                                                    errors.options?.[index]?.value
                                                                }
                                                            />
                                                            <IconButton
                                                                color="error"
                                                                onClick={() => remove(index)}
                                                                disabled={values.options.length === 1}
                                                                aria-label="delete option"
                                                            >
                                                                <Icon path={mdiTrashCanOutline} color={'currentColor'} size={1} />
                                                            </IconButton>
                                                        </Box>
                                                    ))
                                                ) : (
                                                    <Button
                                                        type="button"
                                                        size="small"
                                                        variant="contained"
                                                        onClick={() => push({ label: '', value: '' })}
                                                        sx={{ minWidth: 100, padding: '2px 8px', fontSize: '0.75rem' }}
                                                    >
                                                        Add Option
                                                    </Button>
                                                )}
                                                <Button
                                                    type="button"
                                                    size="small"
                                                    variant="contained"
                                                    onClick={() => push({ label: '', value: '' })}
                                                    sx={{ minWidth: 100, padding: '2px 8px', fontSize: '0.75rem' }}
                                                >
                                                    Add Option
                                                </Button>
                                            </Box>
                                        )}
                                    </FieldArray>
                                )}
                                {/* <TextField
                                    label="Field Limit Size"
                                    variant="filled"
                                    name="field_size"
                                    type="number"
                                    value={values?.field_size || ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    onKeyDown={(e) => {
                                        // Prevent letters and symbols like 'e', 'E', '+', '-'
                                        if (['e', 'E', '+', '-'].includes(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    error={errors.field_size && touched.field_size}
                                    helperText={(errors.field_size && touched.field_size) && errors.field_size}
                                /> */}
                                {DISPLAY_MIN_MAX_LENGTH_OPTIONS.includes(values.display_type) && (
                                    <>
                                        <TextField
                                            label="Max Input Length"
                                            variant="filled"
                                            name="max_input"
                                            type="number"
                                            value={values?.max_input || ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            onKeyDown={(e) => {
                                                // Prevent letters and symbols like 'e', 'E', '+', '-'
                                                if (['e', 'E', '+', '-'].includes(e.key)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                            error={errors.max_input && touched.max_input}
                                            helperText={(errors.max_input && touched.max_input) && errors.max_input}
                                        />

                                        <TextField
                                            label="Min Input Length"
                                            variant="filled"
                                            name="min_input"
                                            type="number"
                                            value={values?.min_input || ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            onKeyDown={(e) => {
                                                // Prevent letters and symbols like 'e', 'E', '+', '-'
                                                if (['e', 'E', '+', '-'].includes(e.key)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                            error={errors.min_input && touched.min_input}
                                            helperText={(errors.min_input && touched.min_input) && errors.min_input}
                                        />
                                    </>
                                )}

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="is_required"
                                            checked={values.is_required === 'yes'}
                                            onChange={(e) =>
                                                setFieldValue('is_required', e.target.checked ? 'yes' : 'no')
                                            }
                                        />
                                    }
                                    label="Required"
                                />
                                {DISPLAY_ALLOW_MULTIPLE_OPTIONS.includes(values.display_type) && (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="is_multiple"
                                                checked={values.is_multiple === 'yes'}
                                                onChange={(e) =>
                                                    setFieldValue('is_multiple', e.target.checked ? 'yes' : 'no')
                                                }
                                            />
                                        }
                                        label="Allow Multiple"
                                    />
                                )}

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="collect_data_on_add"
                                            checked={values.collect_data_on_add === 'yes'}
                                            onChange={(e) =>
                                                setFieldValue('collect_data_on_add', e.target.checked ? 'yes' : 'no')
                                            }
                                        />
                                    }
                                    label="Collect Data During Creation"
                                />

                                {/* <TextField
                                    select
                                    label="Where To Display"
                                    value={values.display_at}
                                    name="display_at"
                                    variant="filled"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.display_at && touched.display_at}
                                    helperText={(errors.display_at && touched.display_at) && errors.display_at}
                                >
                                    <MenuItem value={'list'}>List View</MenuItem>
                                    <MenuItem value={'add'}>Add View</MenuItem>
                                    <MenuItem value={'edit'}>Edit View</MenuItem>
                                    <MenuItem value={'both'}>Both View (Add & Edit)</MenuItem>
                                    <MenuItem value={'all'}>All View</MenuItem>
                                </TextField>

                                <TextField
                                    select
                                    label="Display Field Type"
                                    value={values.display_field_type}
                                    name="display_field_type"
                                    variant="filled"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.display_field_type && touched.display_field_type}
                                    helperText={(errors.display_field_type && touched.display_field_type) && errors.display_field_type}
                                >
                                    <MenuItem value={'normal'}>Normal View</MenuItem>
                                    <MenuItem value={'inline'}>Inline View</MenuItem>
                                    <MenuItem value={'both'}>Both View</MenuItem>
                                </TextField> */}


                                <Grid container spacing={2} sx={{ padding: '10px 0px 0px 0px' }}>
                                    <Grid item xs={6}>
                                        <Button type="sumbit" className="btn-block">Create</Button>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Button color="secondary" className="btn-block" onClick={onClose}>Cancel</Button>
                                    </Grid>
                                </Grid>
                            </form>
                        );
                    }}
                </Formik>
            </Dialog>
        </>
    );
}

export default CreateNewCustomFieldDialog;